import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { T, useT } from '@transifex/react'
import { trackUserEvent } from '@vend/utilities'
import { $state } from '../../../../ng-compat/imports'
import { STATES } from '../../../../../../lib/routes'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import { WEB_REGISTER } from '../../../web-register.constant'
import { isActiveSale, isConfirmedSale } from '../../../../functions/sale'
import { usePaymentManager, useSaleManager } from '../../../../ng-compat/hooks'
import { SaleStatus } from '../../../../models/sale.constants'

export type ModalContextType =
  | 'DEFAULT'
  | 'SWITCH_REGISTERS'
  | 'TRAINING_MODE'
  | 'CLOSE_REGISTER'

interface ConfirmSaleAndFinaliseModalProps {
  close: () => void
  context?: ModalContextType
}

export const ConfirmSaleAndFinaliseModal: FunctionComponent<ConfirmSaleAndFinaliseModalProps> = ({
  close,
  context = 'DEFAULT',
}) => {
  const saleManager = useSaleManager()
  const paymentManager = usePaymentManager()
  const currentSale = useAppSelector(selectCurrentSale)
  const t = useT()
  const [disabled, setDisabled] = useState(false)

  const returnToSale = () => {
    close()
    $state.go(STATES.SALE)
  }

  const saveSale = async () => {
    if (!isActiveSale(currentSale)) {
      close()
      return
    }
    setDisabled(true)

    if (currentSale.isSavedUnfulfilled || currentSale.isSavedService) {
      await saleManager.finaliseSale()
      ToastNotifications.success(t('Your sale was successfully saved'))
      return close()
    }

    if (!isConfirmedSale(currentSale)) {
      const {
        intentId,
        registerSalePaymentId,
      } = paymentManager.checkAndRetriveActivePayment(currentSale.localId)

      if (registerSalePaymentId) {
        trackUserEvent(
          WEB_REGISTER.APPLICATION_NAME,
          'Skip LSPAY Transaction recovery: Opting to proceed with alternative sales',
          {
            paymentIntentId: intentId,
            registerSalePaymentId,
            saleId: currentSale.localId,
          }
        )
      }

      await saleManager.parkSale()
      return close()
    }

    if (currentSale.status === SaleStatus.LAYBY_SALE) {
      await saleManager.laybySale()
    } else {
      await saleManager.onAccountSale()
    }
    await saleManager.finaliseSale()
    close()
    ToastNotifications.success(t('Your sale was successfully saved'))
    setDisabled(false)
  }

  useEffect(() => {
    trackUserEvent(WEB_REGISTER.APPLICATION_NAME, 'Active Sale Confirmation', {
      action: 'actionConfirmSale',
      context,
    })
  }, [context])

  const modalContent = useMemo(() => {
    return {
      DEFAULT: t(
        `You have a sale on the Sell screen that hasn’t been completed. You can choose to return to that sale to complete it, or save that sale and continue with this one`
      ),
      SWITCH_REGISTERS: t(
        `Before you can switch registers, you have to complete the sale on the Sell screen. You can choose to save that sale and continue on to switch registers, or return to that sale to complete it`
      ),
      TRAINING_MODE: t(
        `Before you can switch to Training Mode, you have to complete the sale on the Sell screen. To keep the changes you’ve made to it, return to that sale to complete it. Or you can choose to discard changes made to that sale and switch to Training Mode`
      ),
      CLOSE_REGISTER: t(
        `Before you can close your register, you have to complete the sale on the Sell screen. You can choose to save that sale and continue to close your register, or return to that sale to complete it`
      ),
    }
  }, [t])

  return (
    <Modal onClose={close} size="small">
      <ModalHeader>
        <T _str="Hold up! You currently have a sale in progress" />
      </ModalHeader>
      <ModalContent>
        <Text tag="p">{modalContent[context]}</Text>
        {currentSale.payAccountBalance ? (
          <Text tag="p">
            <T _str="If you save that sale, the Account Balance Payment you’ve added in the Pay screen won’t be remembered when you come back to it" />
          </Text>
        ) : null}
      </ModalContent>
      <ModalActions>
        <div className="vd-btn-group">
          <Button
            loading={disabled}
            variant="supplementary"
            onClick={returnToSale}
          >
            <T _str="Return to Sale in Progress" />
          </Button>
          <Button loading={disabled} onClick={saveSale}>
            <T _str="Save and Continue" />
          </Button>
        </div>
      </ModalActions>
    </Modal>
  )
}
